import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { AlertCircle, ArrowLeft, RefreshCw, ShieldCheck } from 'lucide-react';
import api from '../components/api';

const VerifyOTPPage = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const email = location.state?.email;

    const [digits, setDigits] = useState(['', '', '', '', '', '']);
    const [loading, setLoading] = useState(false);
    const [resending, setResending] = useState(false);
    const [error, setError] = useState('');
    const [countdown, setCountdown] = useState(60);
    const [canResend, setCanResend] = useState(false);
    const inputRefs = useRef([]);

    // Redirect if no email passed
    useEffect(() => {
        if (!email) navigate('/mot-de-passe-oublie');
    }, [email, navigate]);

    // Countdown timer for resend
    useEffect(() => {
        if (countdown <= 0) { setCanResend(true); return; }
        const timer = setTimeout(() => setCountdown(c => c - 1), 1000);
        return () => clearTimeout(timer);
    }, [countdown]);

    const handleDigitChange = (index, value) => {
        if (!/^\d*$/.test(value)) return;
        const newDigits = [...digits];
        newDigits[index] = value.slice(-1);
        setDigits(newDigits);
        setError('');
        if (value && index < 5) {
            inputRefs.current[index + 1]?.focus();
        }
    };

    const handleKeyDown = (index, e) => {
        if (e.key === 'Backspace' && !digits[index] && index > 0) {
            inputRefs.current[index - 1]?.focus();
        }
    };

    const handlePaste = (e) => {
        const paste = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
        if (paste.length > 0) {
            const newDigits = [...digits];
            paste.split('').forEach((char, i) => { if (i < 6) newDigits[i] = char; });
            setDigits(newDigits);
            inputRefs.current[Math.min(paste.length, 5)]?.focus();
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const code = digits.join('');
        if (code.length < 6) {
            setError('Veuillez entrer le code à 6 chiffres');
            return;
        }

        setLoading(true);
        setError('');
        try {
            const response = await api.post('utilisateur/password-reset/verify-otp/', { email, code });
            navigate('/reinitialiser-mot-de-passe', {
                state: { email, reset_token: response.data.reset_token }
            });
        } catch (err) {
            const status = err.response?.status;
            let msg = err.response?.data?.error || 'Code incorrect ou expiré';
            if (status === 400 && msg.includes('expir')) {
                msg = 'Ce code a expiré. Cliquez sur "Renvoyer le code" pour en obtenir un nouveau.';
            }
            setError(msg);
            setDigits(['', '', '', '', '', '']);
            inputRefs.current[0]?.focus();
        } finally {
            setLoading(false);
        }
    };

    const handleResend = async () => {
        setResending(true);
        setError('');
        try {
            await api.post('utilisateur/password-reset/send-otp/', { email });
            setCountdown(60);
            setCanResend(false);
            setDigits(['', '', '', '', '', '']);
            inputRefs.current[0]?.focus();
        } catch (err) {
            const msg = err.response?.data?.error || "Impossible d'envoyer le code. Vérifiez votre connexion.";
            setError(msg);
        } finally {
            setResending(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-orange-50 to-green-50 py-8 px-4">
            <div className="max-w-md w-full bg-white p-8 rounded-2xl shadow-xl">
                <div className="mb-6 text-center">
                    <img className="mx-auto h-14 w-auto" src="/logo-anpe.png" alt="ANPE Logo" />
                    <div className="mt-4 flex justify-center">
                        <div className="p-3 bg-orange-50 rounded-full">
                            <ShieldCheck className="h-10 w-10 text-orange-500" />
                        </div>
                    </div>
                    <h2 className="mt-3 text-2xl font-bold text-gray-900">Vérification du code</h2>
                    <p className="mt-1 text-sm text-gray-600">
                        Entrez le code à 6 chiffres envoyé à
                    </p>
                    <p className="text-sm font-semibold text-orange-600">{email}</p>
                </div>

                {error && (
                    <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center">
                        <AlertCircle className="h-5 w-5 text-red-500 mr-2 flex-shrink-0" />
                        <p className="text-sm text-red-600">{error}</p>
                    </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-3 text-center">
                            Code de vérification
                        </label>
                        <div className="flex justify-center gap-2" onPaste={handlePaste}>
                            {digits.map((digit, index) => (
                                <input
                                    key={index}
                                    ref={el => inputRefs.current[index] = el}
                                    type="text"
                                    inputMode="numeric"
                                    maxLength={1}
                                    value={digit}
                                    onChange={e => handleDigitChange(index, e.target.value)}
                                    onKeyDown={e => handleKeyDown(index, e)}
                                    className={`w-12 h-14 text-center text-xl font-bold border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 transition ${
                                        digit ? 'border-orange-400 bg-orange-50' : 'border-gray-300'
                                    } ${error ? 'border-red-300' : ''}`}
                                />
                            ))}
                        </div>
                        <p className="text-xs text-gray-500 text-center mt-2">
                            Ce code est valable pendant 15 minutes
                        </p>
                    </div>

                    <button
                        type="submit"
                        disabled={loading || digits.join('').length < 6}
                        className={`w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white ${
                            loading || digits.join('').length < 6
                                ? 'bg-orange-300 cursor-not-allowed'
                                : 'bg-orange-600 hover:bg-orange-700'
                        } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 shadow-sm transition`}
                    >
                        {loading ? (
                            <span className="flex items-center">
                                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Vérification...
                            </span>
                        ) : 'Vérifier le code'}
                    </button>
                </form>

                <div className="mt-4 text-center">
                    {canResend ? (
                        <button
                            onClick={handleResend}
                            disabled={resending}
                            className="inline-flex items-center text-sm text-orange-600 hover:text-orange-500 font-medium"
                        >
                            <RefreshCw className={`h-4 w-4 mr-1 ${resending ? 'animate-spin' : ''}`} />
                            {resending ? 'Envoi en cours...' : 'Renvoyer le code'}
                        </button>
                    ) : (
                        <p className="text-sm text-gray-500">
                            Renvoyer le code dans <span className="font-semibold text-orange-500">{countdown}s</span>
                        </p>
                    )}
                </div>

                <div className="mt-4 text-center">
                    <Link to="/mot-de-passe-oublie" className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700">
                        <ArrowLeft className="h-4 w-4 mr-1" />
                        Changer d'adresse email
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default VerifyOTPPage;
